import { apiRequest } from "./api-request";

// Settings·Model → BFF (/ai/model-config), which writes the selection into gemstone; model-gateway
// reads it back. Mutating → carries the CSRF token + session cookie. Only non-empty keys are sent —
// a blank field leaves that provider's stored key unchanged.

export type ModelProvider = "mistral" | "openai" | "anthropic";

// The hosted web search's connectors (model-gateway `WEB_SEARCH_TOOLS`): the plain search engine, or the
// premium one with verified news sources. The admin's switch per provider is `enabled` + the connector.
export type WebSearchTool = "web_search" | "web_search_premium";

export interface WebSearchSetting {
  enabled: boolean;
  tool: WebSearchTool;
}

// `status` is the HTTP status of the failed save (0 for a network/transport failure), so the UI can
// tell "not admin" (403) from "not configured" (501) from "config store unreachable" (502).
export class ModelConfigError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

// Current persisted state for the settings page: the active provider and each provider's stored API
// key (so the fields pre-fill on load). `provider` is null when nothing has been saved yet or gemstone
// isn't wired; `keys` only contains providers that have a stored key.
export interface ModelConfigState {
  provider: ModelProvider | null;
  keys: Partial<Record<ModelProvider, string>>;
  // Only providers the admin has ever set; absent = the gateway's default (off, plain search).
  webSearch?: Partial<Record<ModelProvider, WebSearchSetting>>;
}

export async function loadModelConfig(): Promise<ModelConfigState> {
  const { response } = await apiRequest("ai/model-config", { method: "GET" });
  if (!response.ok)
    throw new ModelConfigError(response.status, `Failed to load model settings (${response.status})`);
  return (await response.json()) as ModelConfigState;
}

export async function saveModelConfig(input: {
  provider: ModelProvider;
  keys: Partial<Record<ModelProvider, string>>;
  webSearch?: Partial<Record<ModelProvider, WebSearchSetting>>;
}): Promise<void> {
  const { response } = await apiRequest("ai/model-config", {
    method: "POST",
    csrf: true,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok)
    throw new ModelConfigError(response.status, `Failed to save model settings (${response.status})`);
}
